<template>
  <transition name="collapse"
              @before-enter="beforeEnter"
              @enter="enter"
              @after-enter="afterEnter"
              @before-leave="beforeLeave"
              @leave="leave"
              @after-leave="afterLeave">
    <div v-if="!collapsed"
         ref="holder">
      <slot />
    </div>
  </transition>
</template>

<script>
import { TweenMax } from "gsap";
// Param reassign is how Vue transitions are done,
// so disable Eslint rule in this instance.
/* eslint-disable no-param-reassign */
/**
 * A reusable collapse transition that is used
 * anywhere there's a collapsible panel or other element.
 */
export default {
  name: "CollapseTransition",
  props: {
    /**
     * Is the element in its collapsed state?
     */
    collapsed: {
      type: Boolean,
      default: false
    },
    /**
     * How long should the collapse/expand animation last?
     * (milliseconds)
     */
    duration: {
      type: Number,
      default: 200
    },
    /**
     * Are we animating the width or height?
     */
    animatedProperty: {
      type: String,
      default: "height"
    },
    /**
     * Should the opacity also fade in/out whilst
     * collapsing/expanding?
     */
    fade: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      height: 0,
      width: 0
    };
  },
  computed: {
    /**
     * A shorthand for turning the animated property
     * to upper case.
     */
    upperProperty () {
      return this.animatedProperty.toUpperCase();
    },
    /**
     * A quick calculation to get the duration in seconds.
     */
    durationSeconds () {
      return this.duration / 1000;
    }
  },
  /**
   * The 'mounted' lifecycle hook.
   * Used to get the initial element measurements.
   * (Cannot happen in 'created' as the DOM elements wont exist yet)
   */
  mounted () {
    this.getElementMeasurements();
  },
  methods: {
    /**
     * Get the rendered measurements of the
     * referenced holder component.
     */
    getElementMeasurements () {
      if ( this.$refs.holder ) {
        if ( this.upperProperty === "WIDTH" ) {
          this.width = this.$refs.holder.clientWidth;
        } else {
          this.height = this.$refs.holder.clientHeight;
        }
      }
    },
    /**
     * Before the 'enter' animations,
     * we need to set some properties on the element.
     */
    beforeEnter ( el ) {
      el.style.overflow = "hidden";
    },
    /**
     * Run the 'enter' animation.
     */
    enter ( el, done ) {
      this.getElementMeasurements();
      this.width = el.clientWidth;
      this.height = el.clientHeight;
      TweenMax.fromTo(
        el,
        this.durationSeconds,
        {
          [this.animatedProperty]: 0,
          opacity: this.fade ? 0 : 1
        },
        {
          [this.animatedProperty]: this[this.animatedProperty],
          opacity: 1,
          onComplete: done
        }
      );
    },
    /**
     * After the 'enter' animations,
     * we need to reset some properties on the element.
     */
    afterEnter ( el ) {
      el.style.width = null;
      el.style.height = null;
      el.style.overflow = null;
    },
    /**
     * Before the 'leave' animations,
     * we need to set some properties on the element.
     */
    beforeLeave ( el ) {
      el.style.overflow = "hidden";
    },
    /**
     * Run the 'leave' animation.
     */
    leave ( el, done ) {
      this.getElementMeasurements();
      TweenMax.fromTo(
        el,
        this.durationSeconds,
        {
          [this.animatedProperty]: this[this.animatedProperty],
          opacity: 1
        },
        {
          [this.animatedProperty]: 0,
          opacity: this.fade ? 0 : 1,
          onComplete: done
        }
      );
    },
    /**
     * After the 'leave' animations,
     * we need to reset some properties on the element.
     */
    afterLeave ( el ) {
      el.style.width = null;
      el.style.height = null;
      el.style.overflow = null;
    }
  }
};
</script>
